import { useEffect, useState } from 'react'
import { supabase } from '../api/supabase'
import { useToast } from '../hooks/useToast'
import type { BoxtopAmbiguousParty } from '../hooks/useSyncToBoxtrax'

interface Props {
  parties: BoxtopAmbiguousParty[]
  onResolved: () => void
  onCancel: () => void
  // Job-sync retry ("Confirm & Retry Sync") vs. Customer detail pre-associate ("Confirm").
  confirmLabel?: string
}

const ROLE_LABELS: Record<string, string> = {
  consignor: 'Consignor / Shipper',
  importer: 'Importer / Consignee',
  notify: 'Notify Party',
  invoiceTo: 'Customer',
  customer: 'Customer',
}

type Decision = { type: 'use'; accountNumber: string } | { type: 'create_new' }

// Z24 (Option C, hybrid auto + confirm): shown when sync-to-boxtrax returns
// 'ambiguous_contact_match' -- one or more parties are 85-95% similar (or have multiple 85%+
// candidates) to an existing BoxTop Contact, so staff must pick the right one rather than risk
// WriteNewContact creating a duplicate for the same physical company. Writes the decision
// straight to customers.boxtop_account_number (or just boxtop_contact_reviewed_at for "create
// new", so the same candidates aren't re-surfaced on the next retry) and lets the caller retry.
export default function BoxtopContactMatchDialog({ parties, onResolved, onCancel, confirmLabel = 'Confirm & Retry Sync' }: Props) {
  const [decisions, setDecisions] = useState<Record<string, Decision>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { toast } = useToast()

  const allDecided = parties.every(p => decisions[p.customerId])
  // Z58.4: couldn't reproduce "Confirm stays disabled after picking a radio" by reading the
  // code (onChange/disabled logic both look correct, traced into the deployed bundle too) --
  // logging real state on every click so the next test run gives hard evidence instead of a
  // third guess.
  console.log('[BoxtopContactMatchDialog] render -- decisions:', decisions, 'allDecided:', allDecided)

  // Z58.6: mount/unmount tracing -- if the dialog is silently remounting (e.g. the caller
  // re-renders with a fresh `parties` array reference and something upstream keys off it), the
  // `decisions` state above would reset to {} right after a click, which would look from the
  // outside exactly like "Confirm stays disabled after picking a radio". A repeated "mounted"
  // log between a candidate click and the stuck Confirm click would confirm that theory.
  useEffect(() => {
    console.log('[BoxtopContactMatchDialog] mounted -- parties:', parties.map(p => ({ role: p.role, customerId: p.customerId, candidateCount: p.candidates.length })))
    return () => console.log('[BoxtopContactMatchDialog] unmounted')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const confirm = async () => {
    setSaving(true); setError(null)
    try {
      const now = new Date().toISOString()
      for (const p of parties) {
        const decision = decisions[p.customerId]
        if (!decision) continue
        const updates = decision.type === 'use'
          ? { boxtop_account_number: decision.accountNumber, boxtop_contact_reviewed_at: now }
          : { boxtop_contact_reviewed_at: now }
        const { error: err } = await supabase.from('customers').update(updates).eq('id', p.customerId)
        if (err) throw err
      }
      toast('BoxTop contact match saved', 'success')
      onResolved()
    } catch (err: any) {
      setError(err?.message || err?.error_description || String(err) || 'Failed to save match decision')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-start gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Possible BoxTop Contact Match</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              These company names look similar to Contacts BoxTop already has — pick the right one to avoid creating a duplicate.
            </p>
          </div>
          <button onClick={onCancel} disabled={saving} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 disabled:opacity-50 shrink-0">
            ✕
          </button>
        </div>

        <div className="p-6 space-y-6">
          {error && <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm">{error}</div>}

          {parties.map(p => {
            const decision = decisions[p.customerId]
            return (
              <div key={p.customerId} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
                <div>
                  <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide">{ROLE_LABELS[p.role] || p.role}</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{p.customerName}</p>
                  {p.customerAddress && <p className="text-xs text-gray-500 dark:text-gray-400">{p.customerAddress}</p>}
                </div>

                <div className="space-y-2">
                  {p.candidates.map(c => (
                    <label
                      key={c.accountNumber}
                      className={`flex items-center justify-between gap-3 p-2.5 rounded-lg border cursor-pointer text-sm ${
                        decision?.type === 'use' && decision.accountNumber === c.accountNumber
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name={`match-${p.customerId}`}
                          checked={decision?.type === 'use' && decision.accountNumber === c.accountNumber}
                          onChange={() => {
                            console.log('[BoxtopContactMatchDialog] candidate radio onChange fired:', p.customerId, c.accountNumber)
                            setDecisions(prev => ({ ...prev, [p.customerId]: { type: 'use', accountNumber: c.accountNumber } }))
                          }}
                        />
                        <div>
                          <p className="font-mono font-semibold text-gray-900 dark:text-white">{c.accountNumber} — {c.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{[c.city, c.country].filter(Boolean).join(', ') || '—'}</p>
                        </div>
                      </div>
                      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 shrink-0">{c.score}% match</span>
                    </label>
                  ))}
                  <label
                    className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer text-sm ${
                      decision?.type === 'create_new'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name={`match-${p.customerId}`}
                      checked={decision?.type === 'create_new'}
                      onChange={() => {
                        console.log('[BoxtopContactMatchDialog] "none of these" radio onChange fired:', p.customerId)
                        setDecisions(prev => ({ ...prev, [p.customerId]: { type: 'create_new' } }))
                      }}
                    />
                    <span className="text-gray-700 dark:text-gray-300">None of these — create a new BoxTop Contact</span>
                  </label>
                </div>
              </div>
            )
          })}
        </div>

        <div className="sticky bottom-0 bg-white dark:bg-gray-800 px-6 py-4 border-t border-gray-200 dark:border-gray-700 space-y-2">
          {/* Z58: the Confirm button disables until every party below has a decision, but with
              more than one party that reason wasn't visible -- surfaces exactly which ones still
              need a pick, so a greyed-out button doesn't look like "can't select it". */}
          {!allDecided && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Still needs a decision: {parties.filter(p => !decisions[p.customerId]).map(p => p.customerName).join(', ')}
            </p>
          )}
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              disabled={saving}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                // Z58.4: this used to be a native `disabled` button -- a disabled button never
                // fires onClick at all, so a genuinely-stuck Confirm produced ZERO console
                // output at the moment of the click, only the render log slightly before/after
                // it (which could already be stale). Logging unconditionally on click, with the
                // exact state read at click-time, tells us definitively whether allDecided is
                // really false right then (and if so, which party's decision didn't register)
                // or whether this is a stale-closure/remount issue instead.
                console.log('[BoxtopContactMatchDialog] Confirm clicked -- saving:', saving, 'allDecided:', allDecided, 'decisions:', decisions,
                  'undecidedParties:', parties.filter(p => !decisions[p.customerId]).map(p => ({ role: p.role, customerId: p.customerId })))
                if (saving || !allDecided) return
                confirm()
              }}
              aria-disabled={saving || !allDecided}
              className={`flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium ${
                saving || !allDecided ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'
              }`}
            >
              {saving ? 'Saving…' : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
